import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Plus, Pencil, Trash2, Heading, Loader2, GripVertical } from 'lucide-react'
import type {
  StyleRule,
  CreateStyleRuleRequest,
  UpdateStyleRuleRequest,
  ComplianceSeverity,
} from '@/lib/api/types'
import { cn } from '@/lib/utils'

interface StyleRulesPanelProps {
  styleRules: StyleRule[]
  loading: boolean
  onAdd: (data: CreateStyleRuleRequest) => Promise<void>
  onUpdate: (ruleId: string, data: UpdateStyleRuleRequest) => Promise<void>
  onDelete: (ruleId: string) => Promise<void>
}

type ElementType = 'Heading1' | 'Heading2' | 'Heading3' | 'Heading4' | 'Heading5' | 'Heading6' | 'Paragraph' | 'Quote' | 'List'

const ELEMENT_TYPES: { value: ElementType; label: string; description: string }[] = [
  { value: 'Heading1', label: 'Heading 1', description: 'Main title' },
  { value: 'Heading2', label: 'Heading 2', description: 'Section headers' },
  { value: 'Heading3', label: 'Heading 3', description: 'Subsections' },
  { value: 'Heading4', label: 'Heading 4', description: 'Sub-subsections' },
  { value: 'Heading5', label: 'Heading 5', description: 'Minor sections' },
  { value: 'Heading6', label: 'Heading 6', description: 'Smallest heading' },
  { value: 'Paragraph', label: 'Paragraph', description: 'Body text' },
  { value: 'Quote', label: 'Quote', description: 'Blockquotes' },
  { value: 'List', label: 'List', description: 'List items' },
]

const FONT_FAMILIES = [
  'Arial',
  'Calibri',
  'Cambria',
  'Georgia',
  'Helvetica',
  'Inter',
  'Lato',
  'Montserrat',
  'Open Sans',
  'Poppins',
  'Roboto',
  'Source Sans Pro',
  'Times New Roman',
  'Verdana',
]

const FONT_WEIGHTS = [
  { value: 100, label: 'Thin (100)' },
  { value: 200, label: 'Extra Light (200)' },
  { value: 300, label: 'Light (300)' },
  { value: 400, label: 'Regular (400)' },
  { value: 500, label: 'Medium (500)' },
  { value: 600, label: 'Semi Bold (600)' },
  { value: 700, label: 'Bold (700)' },
  { value: 800, label: 'Extra Bold (800)' },
  { value: 900, label: 'Black (900)' },
]

const defaultFormData: CreateStyleRuleRequest = {
  elementType: 'Heading1',
  fontFamily: undefined,
  fontSize: undefined,
  fontWeight: undefined,
  lineHeight: undefined,
  marginTop: undefined,
  marginBottom: undefined,
  color: undefined,
  isRequired: false,
  severity: 'Warning',
}

export function StyleRulesPanel({
  styleRules,
  loading,
  onAdd,
  onUpdate,
  onDelete,
}: StyleRulesPanelProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<StyleRule | null>(null)
  const [deleteRule, setDeleteRule] = useState<StyleRule | null>(null)
  const [formData, setFormData] = useState<CreateStyleRuleRequest>(defaultFormData)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleOpenDialog = (rule?: StyleRule) => {
    if (rule) {
      setEditingRule(rule)
      setFormData({
        elementType: rule.elementType,
        fontFamily: rule.fontFamily,
        fontSize: rule.fontSize,
        fontWeight: rule.fontWeight,
        lineHeight: rule.lineHeight,
        marginTop: rule.marginTop,
        marginBottom: rule.marginBottom,
        color: rule.color,
        isRequired: rule.isRequired,
        severity: rule.severity,
      })
    } else {
      setEditingRule(null)
      setFormData(defaultFormData)
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingRule(null)
    setFormData(defaultFormData)
  }

  const handleSave = async () => {
    if (!formData.elementType) return

    setSaving(true)
    try {
      if (editingRule) {
        await onUpdate(editingRule.id, formData as UpdateStyleRuleRequest)
      } else {
        await onAdd(formData)
      }
      handleCloseDialog()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteRule) return

    setDeleting(true)
    try {
      await onDelete(deleteRule.id)
      setDeleteRule(null)
    } finally {
      setDeleting(false)
    }
  }

  const getSeverityBadge = (severity: ComplianceSeverity) => {
    const styles = {
      Error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
      Warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
      Info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
    }
    return <Badge className={cn('text-xs', styles[severity])}>{severity}</Badge>
  }

  const getElementIcon = (elementType: ElementType) => {
    const isHeading = elementType.startsWith('Heading')
    return isHeading ? (
      <Heading className="h-5 w-5 text-neutral-500" />
    ) : (
      <span className="text-sm text-neutral-500 font-medium">
        {elementType === 'Quote' ? 'Q' : elementType === 'List' ? 'L' : 'P'}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading style rules...</p>
      </div>
    )
  }

  // Group rules by type
  const headingRules = styleRules.filter((r) => r.elementType.startsWith('Heading'))
  const otherRules = styleRules.filter((r) => !r.elementType.startsWith('Heading'))

  const renderRuleCard = (rule: StyleRule) => (
    <div
      key={rule.id}
      className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700"
    >
      <GripVertical className="h-4 w-4 text-neutral-400 shrink-0 cursor-grab" />
      <div className="w-8 h-8 rounded flex items-center justify-center bg-neutral-100 dark:bg-neutral-700">
        {getElementIcon(rule.elementType)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-neutral-900 dark:text-neutral-100">
            {ELEMENT_TYPES.find((t) => t.value === rule.elementType)?.label || rule.elementType}
          </span>
          {getSeverityBadge(rule.severity)}
          {rule.isRequired && (
            <Badge variant="secondary" className="text-xs">
              Required
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500 dark:text-neutral-400 flex-wrap">
          {rule.fontFamily && <span>{rule.fontFamily}</span>}
          {rule.fontSize && <span>{rule.fontSize}px</span>}
          {rule.fontWeight && <span>Weight: {rule.fontWeight}</span>}
          {rule.lineHeight && <span>LH: {rule.lineHeight}</span>}
          {(rule.marginTop !== undefined || rule.marginBottom !== undefined) && (
            <span>
              Margin: {rule.marginTop || 0}px / {rule.marginBottom || 0}px
            </span>
          )}
          {rule.color && (
            <span className="flex items-center gap-1">
              <span
                className="w-3 h-3 rounded-full border border-neutral-300 dark:border-neutral-600"
                style={{ backgroundColor: rule.color }}
              />
              {rule.color}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon-sm" onClick={() => handleOpenDialog(rule)}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setDeleteRule(rule)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/30"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            Style Rules
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            Define heading styles, paragraph formatting, and spacing
          </p>
        </div>
        <Button size="sm" onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-1" />
          Add Style
        </Button>
      </div>

      {styleRules.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
          <Heading className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No style rules defined</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
            Add style rules to enforce consistent formatting
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Heading Styles */}
          {headingRules.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                Heading Styles
              </h4>
              <div className="space-y-2">
                {headingRules.sort((a, b) => a.order - b.order).map(renderRuleCard)}
              </div>
            </div>
          )}

          {/* Other Styles */}
          {otherRules.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                Other Styles
              </h4>
              <div className="space-y-2">
                {otherRules.sort((a, b) => a.order - b.order).map(renderRuleCard)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Style Rule' : 'Add Style Rule'}</DialogTitle>
            <DialogDescription>
              Configure element style properties and constraints.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Element Type</Label>
              <Select
                value={formData.elementType}
                onValueChange={(value) => setFormData({ ...formData, elementType: value as ElementType })}
                disabled={!!editingRule}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select element type" />
                </SelectTrigger>
                <SelectContent>
                  {ELEMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <span className="font-medium">{type.label}</span>
                        <span className="text-xs text-neutral-500 ml-2">{type.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Font Family (optional)</Label>
              <Select
                value={formData.fontFamily || ''}
                onValueChange={(value) => setFormData({ ...formData, fontFamily: value || undefined })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any font" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Any font</SelectItem>
                  {FONT_FAMILIES.map((font) => (
                    <SelectItem key={font} value={font}>
                      <span style={{ fontFamily: font }}>{font}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Font Size (px)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 16"
                  value={formData.fontSize || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fontSize: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Font Weight</Label>
                <Select
                  value={formData.fontWeight?.toString() || ''}
                  onValueChange={(value) =>
                    setFormData({ ...formData, fontWeight: value ? parseInt(value) : undefined })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any weight" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Any weight</SelectItem>
                    {FONT_WEIGHTS.map((weight) => (
                      <SelectItem key={weight.value} value={weight.value.toString()}>
                        {weight.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Line Height</Label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="e.g., 1.5"
                  value={formData.lineHeight || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      lineHeight: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Text Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.color || '#000000'}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-10 h-10 rounded border border-neutral-200 dark:border-neutral-700 cursor-pointer"
                  />
                  <Input
                    value={formData.color || ''}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value || undefined })}
                    placeholder="#000000"
                    className="font-mono flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Margin Top (px)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 24"
                  value={formData.marginTop ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      marginTop: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Margin Bottom (px)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 16"
                  value={formData.marginBottom ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      marginBottom: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Severity</Label>
              <Select
                value={formData.severity}
                onValueChange={(value) =>
                  setFormData({ ...formData, severity: value as ComplianceSeverity })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Error">Error - Block non-compliant</SelectItem>
                  <SelectItem value="Warning">Warning - Show warning</SelectItem>
                  <SelectItem value="Info">Info - Informational only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Required Style</Label>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  This style configuration must be applied
                </p>
              </div>
              <Switch
                checked={formData.isRequired}
                onCheckedChange={(checked) => setFormData({ ...formData, isRequired: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !formData.elementType}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingRule ? 'Save Changes' : 'Add Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteRule} onOpenChange={(open) => !open && setDeleteRule(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Style Rule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the style rule for "
              {ELEMENT_TYPES.find((t) => t.value === deleteRule?.elementType)?.label ||
                deleteRule?.elementType}
              "? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRule(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
